import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { businessUnitHooks, portfolioHooks } from './hooks';
const directorRoleOptions = [
    {
        value: 'VP',
        label: 'VP'
    },
    {
        value: 'SPfD',
        label: 'SPfD'
    },
    {
        value: 'PfD',
        label: 'PfD'
    }
];
export function PortfoliosTab() {
    const tenantId = useTenantId();
    const busQuery = businessUnitHooks.useList({
        companyId: tenantId
    });
    const { data, isLoading } = portfolioHooks.useList({
        companyId: tenantId
    });
    const upsert = portfolioHooks.useUpsert();
    const remove = portfolioHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    // Store the selected id and DERIVE the record from `items` (no re-sync effect).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((p)=>p.id === detailId) ?? null : null;
    const [detailMode, setDetailMode] = useState('read');
    const [deleting, setDeleting] = useState(null);
    const bus = busQuery.data?.items ?? [];
    const buById = useMemo(()=>{
        const m = new Map();
        for (const b of bus)m.set(b.id, b);
        return m;
    }, [
        bus
    ]);
    const buOptions = useMemo(()=>bus.map((b)=>({
                value: b.id,
                label: b.name
            })), [
        bus
    ]);
    const fields = useMemo(()=>[
            {
                kind: 'select',
                name: 'businessUnitId',
                label: 'Business Unit',
                options: buOptions
            },
            {
                kind: 'text',
                name: 'name',
                label: 'Portfolio name',
                required: true,
                maxLength: 200
            },
            {
                kind: 'text',
                name: 'directorName',
                label: 'Director name',
                maxLength: 200
            },
            {
                kind: 'select',
                name: 'directorRole',
                label: 'Director role',
                options: directorRoleOptions
            }
        ], [
        buOptions
    ]);
    const columns = useMemo(()=>[
            {
                key: 'name',
                header: 'Name'
            },
            {
                key: 'businessUnitId',
                header: 'Business Unit',
                render: (row)=>buById.get(row.businessUnitId)?.name ?? '—'
            },
            {
                key: 'directorName',
                header: 'Director'
            },
            {
                key: 'directorRole',
                header: 'Role'
            }
        ], [
        buById
    ]);
    // Read sections plus, per row, the inline-edit control — reusing the same
    // field kinds/options as the create form.
    const portfolioDetailSections = (p)=>[
            {
                rows: [
                    {
                        label: 'Business Unit',
                        value: buById.get(p.businessUnitId)?.name ?? '—',
                        editField: {
                            kind: 'select',
                            name: 'businessUnitId',
                            label: 'Business Unit',
                            options: buOptions
                        }
                    },
                    {
                        label: 'Name',
                        value: p.name || '—',
                        editField: {
                            kind: 'text',
                            name: 'name',
                            label: 'Portfolio name',
                            required: true,
                            maxLength: 200
                        }
                    },
                    {
                        label: 'Director',
                        value: p.directorName || '—',
                        editField: {
                            kind: 'text',
                            name: 'directorName',
                            label: 'Director name',
                            maxLength: 200
                        }
                    },
                    {
                        label: 'Role',
                        value: p.directorRole || '—',
                        editField: {
                            kind: 'select',
                            name: 'directorRole',
                            label: 'Director role',
                            options: directorRoleOptions
                        }
                    }
                ]
            }
        ];
    const submitPortfolio = async (values, id)=>{
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            businessUnitId: values.businessUnitId,
            name: values.name,
            directorName: values.directorName ?? '',
            directorRole: values.directorRole ?? ''
        });
    };
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            businessUnitId: detail.businessUnitId ?? '',
            name: detail.name ?? '',
            directorName: detail.directorName ?? '',
            directorRole: detail.directorRole ?? ''
        },
        onSubmit: (v)=>submitPortfolio(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    disabled: bus.length === 0,
                    "data-testid": Testids.deliveryOrg.portfolioCreate,
                    children: "New Portfolio"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryOrg.portfoliosTable,
                rowTestid: Testids.deliveryOrg.portfolioRow,
                rowKey: (p)=>p.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryOrg.portfolioEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryOrg.portfolioDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Portfolio",
                fields: fields,
                defaultValues: {
                    id: '',
                    businessUnitId: '',
                    name: '',
                    directorName: '',
                    directorRole: ''
                },
                onSubmit: async (v)=>{
                    await submitPortfolio(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.name || 'Portfolio' : '',
                sections: detail ? portfolioDetailSections(detail) : [],
                testid: Testids.deliveryOrg.portfolioDetailDrawer,
                edit: detailEdit,
                canEdit: true,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Portfolio",
                description: deleting ? `Permanently delete "${deleting.name}"? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
